import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';

import { Routes } from '@app/constants/routes';
import { useTypedNavigate } from '@app/router';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { Button, Input, Typography } from '@app/components';
import { EmailApi } from '@app/domain/email/api/EmailApi';

import { CustomerHeaderCard } from './components/CustomerHeaderCard';
import { CustomerPreferencesBlock } from './components/CustomerPreferencesBlock';
import { CustomerTimeline } from './components/CustomerTimeline';
import { EmailCard } from '../shared/EmailCard';

// admin-emailbeheer blok A stap 5, opdracht §5, Scherm 2 - Per klant. The support tool: "why
// did this customer not get mail X" is answered by the timeline, not a list of sent mail alone.
// Registered under both Routes.admin.email.customer.index (no userId - empty search state, the
// state you land in from the "Per customer" tab) and .detail (:userId - the User Management
// deep link, and where the search box below sends you).
export const AdminEmailCustomerPage: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();

  const overviewQuery = useQuery({
    queryKey: ['admin-email-customer-overview', userId],
    queryFn: () => (userId ? EmailApi.adminGetCustomerEmailOverview(userId) : undefined),
    enabled: !!userId,
  });

  const unblockMutation = useMutation({
    mutationFn: (reason: string) => {
      if (!userId) {
        return Promise.resolve();
      }
      return EmailApi.adminUnblockCustomerAddress(userId, { reason });
    },
    onSuccess: () => overviewQuery.refetch(),
  });

  return (
    <>
      <div className="flex items-end justify-end gap-4">
        <CustomerSearchBox />
      </div>

      <div className="flex flex-col gap-4 pt-5">
        {!userId && (
          <Typography variant="m" className="text-gray-400">
            Find a customer by name or email above.
          </Typography>
        )}

        {(overviewQuery.isLoading || overviewQuery.isError) && (
          <ContentLoader loading={overviewQuery.isLoading} error={overviewQuery.error} />
        )}

        {overviewQuery.data?.header && (
          <CustomerHeaderCard
            header={overviewQuery.data.header}
            onUnblock={(reason) => unblockMutation.mutate(reason)}
            unblockLoading={unblockMutation.isLoading}
          />
        )}

        {overviewQuery.data?.preferences && <CustomerPreferencesBlock preferences={overviewQuery.data.preferences} />}

        {overviewQuery.data && userId && (
          <EmailCard title="Timeline" subtitle="Sent, planned, skipped and held back by SendGrid - chronologically.">
            <CustomerTimeline
              userId={userId}
              timeline={overviewQuery.data.timeline ?? []}
              onActionComplete={() => overviewQuery.refetch()}
            />
          </EmailCard>
        )}
      </div>
    </>
  );
};

// opdracht §5's own "zoeken op naam of e-mailadres" - the primary entry point now that
// checking mail starts from this tab (2026-08-24, Danny's call), not from a link out of User
// Management's customer list. Names aren't unique, so this always shows a pick list rather
// than auto-navigating on a single match.
const CustomerSearchBox: React.FC = () => {
  const navigate = useTypedNavigate();
  const [term, setTerm] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');

  const searchQuery = useQuery(
    ['admin-email-customer-search', submittedTerm],
    () => EmailApi.adminSearchCustomers(submittedTerm),
    { enabled: !!submittedTerm }
  );

  const pickResult = (resultUserId: string) => {
    navigate({ to: Routes.admin.email.customer.detail, params: { userId: resultUserId } });
    setTerm('');
    setSubmittedTerm('');
  };

  return (
    <div className="relative">
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (term.trim()) {
            setSubmittedTerm(term.trim());
          }
        }}
      >
        <Input
          label="Find by name or email"
          placeholder="Marieke or name@example.com"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <Button type="submit" loading={searchQuery.isFetching} size="small">
          Go
        </Button>
      </form>

      {submittedTerm && (
        <div className="absolute right-0 z-10 mt-1 w-72 rounded-md bg-white p-1.5 shadow-card">
          {searchQuery.isFetching && (
            <Typography variant="xs" className="p-2 text-gray-400">
              Searching...
            </Typography>
          )}
          {!searchQuery.isFetching && !searchQuery.data?.length && (
            <Typography variant="xs" className="p-2 text-gray-400">
              No customer found.
            </Typography>
          )}
          {searchQuery.data?.map(
            (result) =>
              result.userId && (
                <button
                  key={result.userId}
                  type="button"
                  className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accept-light"
                  onClick={() => pickResult(result.userId as string)}
                >
                  <div className="font-bold text-gray-800">{result.fullName || '(no name)'}</div>
                  <div className="text-gray-500">{result.email}</div>
                </button>
              )
          )}
        </div>
      )}
    </div>
  );
};
